/**
 * soundPrefs.js — global sound on/off preference (UI_REVIEW §6).
 *
 * Single source of truth for whether Orboot may make ANY sound:
 * soundEngine chimes, Beary's voice, and narration playback all check this
 * before playing. Persisted in localStorage so the choice survives reloads —
 * important for classrooms and bedtimes.
 */
import { useSyncExternalStore } from 'react';

const KEY = 'orboot.sound.v1';

function read() {
  try {
    return window.localStorage.getItem(KEY) !== 'off'; // default: on
  } catch {
    return true;
  }
}

let enabled = read();
const listeners = new Set();

export function isSoundEnabled() {
  return enabled;
}

export function setSoundEnabled(value) {
  enabled = !!value;
  try {
    window.localStorage.setItem(KEY, enabled ? 'on' : 'off');
  } catch {
    /* private mode etc. — keep in-memory value */
  }
  listeners.forEach((fn) => fn());
}

export function toggleSound() {
  setSoundEnabled(!enabled);
  return enabled;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook — re-renders when the preference changes anywhere in the app. */
export function useSoundEnabled() {
  return useSyncExternalStore(subscribe, isSoundEnabled, () => true);
}
